import React from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from 'contexts/AuthContext'
import { ShieldAlert } from 'lucide-react'
import { Button } from 'components/ui/Button'

interface PermissionRouteProps {
  children: React.ReactNode
  required: string | string[]
}

export function PermissionRoute({ children, required }: PermissionRouteProps) {
  const { isAuthenticated, loading, hasPermission, user } = useAuth()

  if (loading) return null
  if (!isAuthenticated) return <Navigate to="/login" replace />

  // Check if user is an organization admin (has organization and is_staff but not superuser)
  const isOrgAdmin = user?.is_staff && !user?.is_superuser && user?.organization

  // Permissions that organization admins should have access to for managing their organization
  const orgAdminPermissions = [
    'view_candidates',
    'create_candidates',
    'edit_candidates',
    'view_offers',
    'create_offers',
    'edit_offers',
    'use_ai_matching',
    'view_users',
    'edit_users',
  ]

  const requiredList = Array.isArray(required) ? required : [required]
  
  // Check if user has permission OR is an org admin with access to this permission
  const hasRequiredPermission = requiredList.some((code) => hasPermission(code))
  const isOrgAdminWithAccess = isOrgAdmin && requiredList.some((code) => orgAdminPermissions.includes(code))
  
  const allowed = hasRequiredPermission || isOrgAdminWithAccess

  if (!allowed) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 px-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <ShieldAlert className="h-8 w-8" />
        </div>
        <div className="space-y-2">
          <p className="text-xl font-semibold">Access restricted</p>
          <p className="text-muted-foreground max-w-md">
            You don&apos;t have permission to view this section. Contact an administrator if you
            think this is a mistake.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <Button asChild>
            <Link to="/">Go to home</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/settings">Manage profile</Link>
          </Button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}


